import React from "react";
import lodashGet from "lodash/get";
import { useSelector } from "react-redux";

import { useDeepEffect, useDeepMemo, useMutation, useQuery, useSetting, useTenant, useUnit } from "../../../../hooks";
import SettingKeys from "../../../../utils/SettingKeys";
import { getDistinctValues, isEmpty, isFalse, isTrue, signedValue, toFixed, toFixedSigned, truncate } from "../../../../utils/helpers";
import { SpreadingType, Entity } from "../../../../utils/types";
import { OrderFlowType, UOM } from "../../../../constants";
import { getEffFlowInfoList, getSizeBreakupList } from "../../../../helpers/orderHelper";
import { convertTableToCell } from "../../../../utils/excelHelper";
import Tooltip from "../../../../components/Tooltip";
import { DataType, getSummaryEchartOptions } from "../../../../helpers/reportHelper";
import { getReportFlowInfoList } from "./reportHelper";
import { getMarkerList } from "../../../cutplan/cutPlanHelper";
const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const TooltipField = ({ column, record }) => {
  const value = record[column?.dataKey];
  return (
    <Tooltip title={value}>
      <span>{truncate(value, 30)}</span>
    </Tooltip>
  );
};

const otherFields = [
  "effPartName",
  "cutQty",
  "rejectedQty",
  "bomCu",
  "layCu",
  "planCu",
  "stdCu",
  "actualCu",
  "grossCu",
  "markerEfficiency",
  "itemCode",
  "itemColor",
  "allocated",
  "returned",
  "excess",
  "shortage",
  "missing",
  "unused",
  "usedInLay",
  "usedInPanel",
  "endLoss",
  "damaged",
  "endBits"
];

// const getApprovedQty = (cutOrder, styleColor) => {
//   if (isEmpty(cutOrder?.orderConfig?.approvedOrderCutQty)) return 0;
//   const approvedOrderCutQtyMap = JSON.parse(cutOrder?.orderConfig?.approvedOrderCutQty || "{}");
//   const approvedQtyMap = approvedOrderCutQtyMap[styleColor] || {};
//   const approvedQty = Object.keys(approvedQtyMap).reduce((acc, key) => acc + approvedQtyMap[key], 0);
//   return approvedQty;
// };

const getColumnsCutSummary = (order, selectedRow) => {
  if (isEmpty(order) || isEmpty(selectedRow)) return { columnsCutSummary: [], headerRows: [] };

  const sizeBreakupList = getSizeBreakupList(selectedRow?.cutOrderList, order);

  const sizeGroups = getDistinctValues(sizeBreakupList.sort((a, b) => a.serialNo - b.serialNo).map(sb => sb.sizeGroup));

  const styleColorIdx = sizeGroups.reduce((acc, e, idx) => ({ ...acc, [e]: idx % 2 }), {});
  const groupedCutPlan = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 1;

  let groupRow = {};
  let sizeGroup;
  const sizeList = sizeBreakupList.map((sb, idx) => {
    let leftBorder = false;
    if (sizeGroup !== sb.sizeGroup) {
      leftBorder = true;
      sizeGroup = sb.sizeGroup;
      groupRow[sb.sizeKey] = sb.sizeGroup;
    }
    return {
      dataKey: sb.sizeKey,
      title: sb.size,
      align: "center",
      styleColorIdx: sizeGroups.length > 1 ? styleColorIdx[sb.sizeGroup] : undefined,
      leftBorder,
      rightBorder: idx === sizeBreakupList.length - 1,
      colSpan: ({ column, row, rowIdx }) =>
        !groupedCutPlan || rowIdx === 1
          ? 1
          : isEmpty(row[column.dataKey])
            ? 0
            : getDistinctValues(sizeBreakupList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
    };
  });

  let columns = [
    { dataKey: "desc", title: "Description", align: "left" },
    ...sizeList,
    { dataKey: "total", title: "Total", leftBorder: true },
    { dataKey: "totalPercent", title: "Total(%)", leftBorder: true }
  ];

  columns = columns.map(c => ({ ...c, align: c.align || "center" }));

  let headerRows = [];
  if (Object.keys(groupRow)?.length > 1) {
    headerRows = [groupRow, columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
  } else {
    headerRows = [columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
  }

  return { columnsCutSummary: columns, headerRows };
};

const getRowsCutSummary = (order, selectedRow, layRowData, excel = false, rejectedData = []) => {
  if (isEmpty(order?.flowInfoList) || isEmpty(selectedRow)) return [];

  const cutOrderList = selectedRow?.cutOrderList || [];

  const effFlowInfoList = cutOrderList.flatMap(cutOrder => getEffFlowInfoList(order, cutOrder));
  const flowInfoMap = effFlowInfoList.reduce((acc, fi) => ({ ...acc, [fi.id]: fi }), {});
  const finalFlowInfoList = Object.values(flowInfoMap).filter(fi => fi.styleColor === selectedRow.styleColor);

  // for order Qty
  const orderFlowInfoMap = effFlowInfoList.reduce((acc, fi) => ({ ...acc, [fi?.orderParentId != null ? fi?.orderParentId : fi.id]: fi }), {});
  const orderFinalFlowInfoList = Object.values(orderFlowInfoMap).filter(fi => fi.styleColor === selectedRow.styleColor);
  const orderSizeBreakupList = orderFinalFlowInfoList.flatMap(fi => fi.sizeBreakupList || []).filter(sb => sb.type === "ORDER");

  const fpoSizeBreakupList = finalFlowInfoList.flatMap(fi => fi.fpoSizeBreakupList || []);
  const sbMap = fpoSizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
  const sizeKeyList = Object.values(sbMap)
    .sort((a, b) => a.serialNo - b.serialNo)
    .map(sb => sb.sizeKey);

  let rows = [];
  // Process Order Qty
  const orderQty = orderSizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }), {});
  let totalOrderQty = Object.keys(orderQty).reduce((acc, key) => acc + orderQty[key], 0);
  rows.push({ ...orderQty, desc: "Order Qty", total: totalOrderQty, totalPercent: `100%` });

  // Process FPO Qty
  const fpoQty = fpoSizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }), {});
  let totalFpoQty = Object.keys(fpoQty).reduce((acc, key) => acc + fpoQty[key], 0);
  if (totalFpoQty !== totalOrderQty) {
    rows.push({
      ...fpoQty,
      desc: "FPO Qty",
      total: totalFpoQty,
      totalPercent: totalOrderQty > 0 ? `${toFixed((totalFpoQty * 100) / totalOrderQty, 1)}%` : ""
    });
  }

  // Process Cut Qty
  const markerList = cutOrderList.flatMap(cutOrder => getMarkerList(cutOrder) || []);
  const layRowList =
    layRowData
      ?.filter(row => isTrue(row.completed) && row.styleColor === selectedRow.styleColor)
      ?.map(row => ({ ...row, marker: markerList?.find(m => m.id === row.markerId) }))
      .filter(e => !isEmpty(e.marker)) || [];

  const cutQty = sizeKeyList.reduce((acc, sizeKey) => {
    const qty = layRowList.reduce((acc, row) => {
      const sizeRatio = row.marker?.sizeRatioList.find(sr => sr.sizeKey === sizeKey);
      const plies = SpreadingType.FACE_ONE_WAY !== row.marker?.spreadingType ? row.actualPly / 2 : row.actualPly;
      const qty = sizeRatio ? sizeRatio.ratio * plies : 0;
      return acc + qty;
    }, 0);
    return { ...acc, [sizeKey]: qty };
  }, {});

  let total = Object.keys(cutQty).reduce((acc, key) => acc + cutQty[key], 0);
  let totalPercent = totalOrderQty > 0 ? `${toFixed((total * 100) / totalOrderQty, 1)}%` : "";
  rows.push({ ...cutQty, desc: `Cut Qty`, total, totalPercent });


  // Rejected Qty
  const rejectedQty = sizeKeyList.map((key) => {
    const perSizeReject =
      rejectedData
        .filter(row => row.styleColor === selectedRow.styleColor)
        .filter(e => e.sizeKey === key);
    const qty = perSizeReject.reduce((acc, curr) => acc + curr?.qty, 0);
    return {
      [key]: qty
    }
  }).reduce((acc, curr) => ({ ...acc, ...curr }), {});

  total = Object.values(rejectedQty)?.reduce((acc, curr) => acc + curr, 0);
  totalPercent = totalOrderQty > 0 ? `${toFixed((total * 100) / totalOrderQty, 1)}%` : "";
  rows.push({ ...rejectedQty, desc: `Rejected Qty`, total, totalPercent });

  // Net Cut Qty
  const netCutQty = sizeKeyList.map((key) => {
    const sizeRejectCutQty = rejectedQty[key] || 0;
    const sizeCutQty = cutQty[key] || 0;
    return { [key]: sizeCutQty - sizeRejectCutQty };
  }).reduce((acc, curr) => ({ ...acc, ...curr }), {});

  total = Object.values(netCutQty)?.reduce((acc, curr) => acc + curr, 0);
  totalPercent = totalOrderQty > 0 ? `${toFixed((total * 100) / totalOrderQty, 1)}%` : "";
  rows.push({ ...netCutQty, desc: `Net Cut Qty`, total, totalPercent });

  // Process Balance Qty w.r.t FPO Qty
  let totalBalance = 0;
  let balanceQty = sizeKeyList.reduce((acc, sizeKey) => {
    const balance = netCutQty[sizeKey] - fpoQty[sizeKey];

    const balancePercent = toFixedSigned((balance * 100) / fpoQty[sizeKey], 1);
    totalBalance += balance;
    return { ...acc, [sizeKey]: excel ? balance : `${signedValue(balance)} (${balancePercent}%)` };
  }, {});

  if (totalFpoQty !== totalOrderQty) {
    rows.push({
      ...balanceQty,
      total: excel ? totalBalance : `${signedValue(totalBalance)}`,
      totalPercent: totalFpoQty > 0 ? `${toFixedSigned((totalBalance * 100) / totalFpoQty, 1)}%` : "",
      desc: "Extra/Short w.r.t FPO Qty"
    });
  }

  // Process Balance Qty w.r.t Order Qty
  totalBalance = 0;
  balanceQty = sizeKeyList.reduce((acc, sizeKey) => {
    const balance = netCutQty[sizeKey] - orderQty[sizeKey];
    const balancePercent = toFixedSigned((balance * 100) / orderQty[sizeKey], 1);
    totalBalance += balance;
    return { ...acc, [sizeKey]: excel ? balance : `${signedValue(balance)} (${balancePercent}%)` };
  }, {});
  rows.push({
    ...balanceQty,
    total: excel ? totalBalance : `${signedValue(totalBalance)}`,
    totalPercent: totalOrderQty > 0 ? `${toFixedSigned((totalBalance * 100) / totalOrderQty, 1)}%` : "",
    desc: totalFpoQty !== totalOrderQty ? "Extra/Short w.r.t Order Qty" : "Extra/Short"
  });

  return rows;
};

const useReconciliationSummary = props => {
  const { order, style, color, reportSummaryList, selectedKey, setSelectedKey } = props;

  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ORDERS);
  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FLOW_INFO);
  const productParts = useSetting(SettingKeys.PRODUCT_PARTS);
  const showStdCuVal = useSetting(SettingKeys.SHOW_STD_CU);
  const showStdCu = isTrue(showStdCuVal);
  const tenant = useTenant();

  const i18nMessages = useSelector(state => state.i18n?.messages || {});

  const [download, { loading: downloading }] = useMutation({ rawResponse: true });

  const cutOrderIds = order?.orderFlowList
    ?.filter(of => of.type === OrderFlowType.CUTPLAN)
    .map(e => e.id)
    .sort();
  const { data: layRowData = [] } = useQuery(
    !isEmpty(cutOrderIds) && { url: "lay-rows", params: { search: `completed=='TRUE';lay.orderFlow.id=in=(${cutOrderIds.join(",")})` } }
  );

  const { data: rejectionData = [] } = useQuery(
    !isEmpty(cutOrderIds) && { url: "rejections", params: { search: `orderFlow.id=in=(${cutOrderIds.join(",")})`, size: 2000 } }
  );

  const { allUnitList } = useUnit();

  const summaryColumns = useDeepMemo(() => {
    if (isEmpty(i18nMessages)) return [];

    let cols = [
      { dataKey: "poRef", title: "ERP No." },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      { dataKey: "productName", title: "Product", filterOptions: { filter: true }, highlight: true },
      { dataKey: "style", title: "Style Name", filterOptions: { filter: true }, highlight: true },
      { dataKey: "styleNo", title: "Style No", filterOptions: { filter: true }, highlight: true },
      { dataKey: "color", title: "Style Color", filterOptions: { filter: true }, highlight: true },
      { dataKey: "effPartName", title: "Part", highlight: true },
      { dataKey: "orderQty", title: "Order Qty", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      { dataKey: "extra", title: "Extra Ship (%)", render: value => (isEmpty(value) ? "0%" : `${value}%`) },
      {
        dataKey: "cutQty",
        title: "Net Cut Qty",
        render: (value, _, row) => (isEmpty(value) ? "" : `${value} ${row.uomQty || "Pc"}`)
      },
      { dataKey: "rejectedQty", title: "Rejected Qty" },
      { dataKey: "bomCu", title: "BOM CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "planCu", title: "Plan CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "stdCu", title: "Std. CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "actualCu", title: "Actual CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "grossCu", title: "Gross CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "markerEfficiency", title: "Marker Efficiency", render: value => (isEmpty(value) ? "-" : `${value}%`) },
      { dataKey: "status", title: "Status" }
    ].map(c => ({ ...c, title: lodashGet(i18nMessages, `resources.report.arr.excelColumns.${c.dataKey}`) || c.title }));
    let fields = [...orderFields, ...flowInfoFields, ...otherFields];
    if (!showStdCu) {
      fields = fields.filter(f => f !== "stdCu");
    }
    return cols.filter(c => fields.includes(c.dataKey));
  }, [orderFields, flowInfoFields, showStdCu, i18nMessages]);

  const fabUsageColumns = useDeepMemo(() => {
    let cols = [
      {
        dataKey: "allocated",
        title: "Allocated",
        leftBorder: true,
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "blanketQty",
        title: "Testing \nLoss",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "issued",
        title: "Fabric \nIssued",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "returned",
        title: "Return \nto W/H",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "leftover",
        title: "Gross \nLeftover",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "used",
        title: "Fabric \nUtilized",
        rightBorder: true,
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "netIssued",
        title: "Net \nIssued",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "shortageExcess",
        title: "Net \nShortage/Excess",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${toFixedSigned(value)} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "usedInMarker",
        title: "Used \nin Marker",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      { dataKey: "endLoss", title: "End Loss", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      {
        dataKey: "grossEndBits",
        title: "Gross \nEnd Bits",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "endBitsUsedInPanel",
        title: "Panel Recut \n(End Bits)",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "netEndBits",
        title: "Net \nEnd Bits",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "rollUsedInPanel",
        title: "Panel Recut \n(Roll)",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "damaged",
        title: "Fabric \nDamage",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "netWastage",
        title: "Net \nWastage",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "missing",
        title: "Missed \nin Cutting",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "unused",
        title: "Unutilized \nby Cutting",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "grossWastage",
        title: "Gross \nWastage*",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      { dataKey: "itemCode", title: "Item Code", element: TooltipField },
      { dataKey: "itemColor", title: "Item Color", element: TooltipField }
    ];
    // const fields = [...orderFields, ...flowInfoFields, ...otherFields];
    return cols;
  }, [orderFields, flowInfoFields]);

  const rows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList)) return [];

    const flowInfoList = getReportFlowInfoList(order);

    const rows = flowInfoList.flatMap(({ part, ...flowInfo }) => {
      if (flowInfo.isParent) {
        return [
          {
            ...flowInfo,
            poRef: order.poRef,
            customerName: order.customer?.name,
            seasonName: order.season?.name,
            productName: flowInfo.product?.name
          }
        ];
      }
      const status = flowInfo.cutOrderList?.sort((a, b) => a.stateValue < b.stateValue)[0]?.state;

      const reportSummary = reportSummaryList.find(
        e =>
          e.productId === flowInfo.product?.id &&
          e.style === flowInfo.style &&
          e.color === flowInfo.color &&
          e.part === part.partName &&
          (isEmpty(part.placement) || part.placement === e.placement)
      );

      if (isEmpty(reportSummary)) return [];

      if (reportSummary?.fabricUom === UOM.KILOGRAM.value) {
        const {
          fabricUom,
          allocatedWeight = 0,
          blanketWeight = 0,
          issuedWeight = 0,
          usedInLayWeight = 0,
          returnedWeight = 0,
          endLossWeight = 0,
          grossEndBitsWeight = 0,
          netEndBitsWeight = 0,
          damagedWeight = 0,
          usedInPanelWeight = 0,
          missingWeight = 0,
          unusedWeight = 0,
          orderingCuInKg = 0
        } = reportSummary;

        const leftover = allocatedWeight + returnedWeight - issuedWeight;
        const used = issuedWeight - returnedWeight + blanketWeight;
        const netWastage = endLossWeight + netEndBitsWeight + damagedWeight + usedInPanelWeight;
        const grossWastage = netWastage + missingWeight + unusedWeight + blanketWeight;

        const netIssued = issuedWeight - returnedWeight;
        const shortageExcess = 0;
        const usedInMarker = usedInLayWeight - endLossWeight;
        const endBitsUsedInPanel = grossEndBitsWeight - netEndBitsWeight;
        const rollUsedInPanel = usedInPanelWeight - endBitsUsedInPanel;

        return [
          {
            ...flowInfo,
            ...reportSummary,
            fabricUom,
            bomCu: orderingCuInKg,
            allocated: allocatedWeight,
            blanketQty: blanketWeight,
            issued: issuedWeight,
            returned: returnedWeight,
            endLoss: endLossWeight,
            grossEndBits: grossEndBitsWeight,
            netEndBits: netEndBitsWeight,
            damaged: damagedWeight,
            missing: missingWeight,
            unused: unusedWeight,
            usedInMarker,
            netWastage,
            leftover,
            used,
            netIssued,
            shortageExcess,
            grossWastage,
            endBitsUsedInPanel,
            rollUsedInPanel,
            productId: flowInfo.product?.id || flowInfo.productId,
            // key: `${flowInfo.id}-${part.effPartName}`,
            // flowInfoList: fiList,
            // cutOrderList,
            status,
            poRef: order.poRef,
            customerName: reportSummary.customer,
            seasonName: reportSummary.season,
            productName: JSON.parse(reportSummary.products).join(", "),
            effPartName: part.effPartName,
            itemColor: `${reportSummary.fabColor}${!isEmpty(reportSummary.fabColorCode) ? ` (${reportSummary.fabColorCode})` : ""}`,
            cutQty: reportSummary?.cutQty - reportSummary?.rejectedQty || 0
          }
        ];
      } else {
        const {
          fabricUom,
          allocated = 0,
          blanketQty = 0,
          issued = 0,
          usedInLay = 0,
          returned = 0,
          endLoss = 0,
          grossEndBits = 0,
          netEndBits = 0,
          damaged = 0,
          usedInPanel = 0,
          missing = 0,
          unused = 0,
          shortage = 0,
          excess = 0,
          orderingCu = 0
        } = reportSummary;

        const leftover = allocated + returned - issued;
        const used = issued - returned + blanketQty;
        const netWastage = endLoss + netEndBits + damaged + usedInPanel;
        const grossWastage = netWastage + missing + unused + blanketQty;

        const netIssued = issued - returned;
        const shortageExcess = shortage + excess;
        const usedInMarker = usedInLay - endLoss;
        const endBitsUsedInPanel = grossEndBits - netEndBits;
        const rollUsedInPanel = usedInPanel - endBitsUsedInPanel;

        return [
          {
            ...flowInfo,
            ...reportSummary,
            fabricUom,
            bomCu: orderingCu,
            usedInMarker,
            netWastage,
            leftover,
            used,
            netIssued,
            shortageExcess,
            grossWastage,
            endBitsUsedInPanel,
            rollUsedInPanel,
            productId: flowInfo.product?.id || flowInfo.productId,
            // key: `${flowInfo.id}-${part.effPartName}`,
            status,
            poRef: order.poRef,
            customerName: reportSummary.customer,
            seasonName: reportSummary.season,
            productName: JSON.parse(reportSummary.products).join(", "),
            effPartName: part.effPartName,
            itemColor: `${reportSummary.fabColor}${!isEmpty(reportSummary.fabColorCode) ? ` (${reportSummary.fabColorCode})` : ""}`,
            cutQty: reportSummary?.cutQty - reportSummary?.rejectedQty || 0
          }
        ];
      }

      // const children = order.flowInfoList.filter(fi => fi.parentId === flowInfo.id);
      // const fiList = isEmpty(children) ? [flowInfo] : children;
      // const partList = getEffPartList(fiList, order);

      // return partList
      //   .map(part => {
      //     const cutOrderList =
      //       order.orderFlowList
      //         ?.filter(of => of.type === OrderFlowType.CUTPLAN)
      //         .filter(
      //           of => of.product?.id === flowInfo.product?.id && of.styleColorList?.includes(flowInfo.styleColor) && of.part === part.effPartName
      //         ) || [];
      //     // const cutOrderIds = cutOrderList.map(e => e.id);
      //     const status = cutOrderList.sort((a, b) => a.stateValue < b.stateValue)[0]?.state;
      //     // const currReportList = reportDetailList.filter(r => cutOrderIds.includes(r.cutOrderId)).filter(r => r.styleColor === flowInfo.styleColor);

      //     const reportSummary = reportSummaryList.find(
      //       e =>
      //         e.productId === flowInfo.product?.id &&
      //         e.style === flowInfo.style &&
      //         e.color === flowInfo.color &&
      //         e.part === part.partName &&
      //         (isEmpty(part.placement) || part.placement === e.placement)
      //     );

      //     if (isEmpty(reportSummary)) return [];

      //     if (reportSummary?.fabricUom === UOM.KILOGRAM.value) {
      //       const {
      //         fabricUom,
      //         allocatedWeight = 0,
      //         blanketWeight = 0,
      //         issuedWeight = 0,
      //         usedInLayWeight = 0,
      //         returnedWeight = 0,
      //         endLossWeight = 0,
      //         grossEndBitsWeight = 0,
      //         netEndBitsWeight = 0,
      //         damagedWeight = 0,
      //         usedInPanelWeight = 0,
      //         missingWeight = 0,
      //         unusedWeight = 0,
      //         orderingCuInKg = 0
      //       } = reportSummary;

      //       const leftover = allocatedWeight + returnedWeight - issuedWeight;
      //       const used = issuedWeight - returnedWeight + blanketWeight;
      //       const netWastage = endLossWeight + netEndBitsWeight + damagedWeight + usedInPanelWeight;
      //       const grossWastage = netWastage + missingWeight + unusedWeight + blanketWeight;

      //       const netIssued = issuedWeight - returnedWeight;
      //       const shortageExcess = 0;
      //       const usedInMarker = usedInLayWeight - endLossWeight;
      //       const endBitsUsedInPanel = grossEndBitsWeight - netEndBitsWeight;
      //       const rollUsedInPanel = usedInPanelWeight - endBitsUsedInPanel;

      //       return {
      //         ...flowInfo,
      //         ...reportSummary,
      //         fabricUom,
      //         bomCu: orderingCuInKg,
      //         allocated: allocatedWeight,
      //         blanketQty: blanketWeight,
      //         issued: issuedWeight,
      //         returned: returnedWeight,
      //         endLoss: endLossWeight,
      //         grossEndBits: grossEndBitsWeight,
      //         netEndBits: netEndBitsWeight,
      //         damaged: damagedWeight,
      //         missing: missingWeight,
      //         unused: unusedWeight,
      //         usedInMarker,
      //         netWastage,
      //         leftover,
      //         used,
      //         netIssued,
      //         shortageExcess,
      //         grossWastage,
      //         endBitsUsedInPanel,
      //         rollUsedInPanel,
      //         productId: flowInfo.product?.id || flowInfo.productId,
      //         key: `${flowInfo.id}-${part.effPartName}`,
      //         flowInfoList: fiList,
      //         cutOrderList,
      //         status,
      //         poRef: order.poRef,
      //         customerName: reportSummary.customer,
      //         seasonName: reportSummary.season,
      //         productName: reportSummary.product,
      //         effPartName: part.effPartName,
      //         itemColor: `${reportSummary.fabColor}${!isEmpty(reportSummary.fabColorCode) ? ` (${reportSummary.fabColorCode})` : ""}`
      //       };
      //     } else {
      //       const {
      //         fabricUom,
      //         allocated = 0,
      //         blanketQty = 0,
      //         issued = 0,
      //         usedInLay = 0,
      //         returned = 0,
      //         endLoss = 0,
      //         grossEndBits = 0,
      //         netEndBits = 0,
      //         damaged = 0,
      //         usedInPanel = 0,
      //         missing = 0,
      //         unused = 0,
      //         shortage = 0,
      //         excess = 0,
      //         orderingCu = 0
      //       } = reportSummary;

      //       const leftover = allocated + returned - issued;
      //       const used = issued - returned + blanketQty;
      //       const netWastage = endLoss + netEndBits + damaged + usedInPanel;
      //       const grossWastage = netWastage + missing + unused + blanketQty;

      //       const netIssued = issued - returned;
      //       const shortageExcess = shortage + excess;
      //       const usedInMarker = usedInLay - endLoss;
      //       const endBitsUsedInPanel = grossEndBits - netEndBits;
      //       const rollUsedInPanel = usedInPanel - endBitsUsedInPanel;

      //       return {
      //         ...flowInfo,
      //         ...reportSummary,
      //         fabricUom,
      //         bomCu: orderingCu,
      //         usedInMarker,
      //         netWastage,
      //         leftover,
      //         used,
      //         netIssued,
      //         shortageExcess,
      //         grossWastage,
      //         endBitsUsedInPanel,
      //         rollUsedInPanel,
      //         productId: flowInfo.product?.id || flowInfo.productId,
      //         key: `${flowInfo.id}-${part.effPartName}`,
      //         flowInfoList: fiList,
      //         cutOrderList,
      //         status,
      //         poRef: order.poRef,
      //         customerName: reportSummary.customer,
      //         seasonName: reportSummary.season,
      //         productName: reportSummary.product,
      //         effPartName: part.effPartName,
      //         itemColor: `${reportSummary.fabColor}${!isEmpty(reportSummary.fabColorCode) ? ` (${reportSummary.fabColorCode})` : ""}`
      //       };
      //     }
      //   })
      //   .filter(e => !isEmpty(e.itemCode));
    });

    return rows;
  }, [order, productParts, reportSummaryList]);
  
  useDeepEffect(() => {
    if (isEmpty(rows) || !isEmpty(selectedKey)) return;
    if (isEmpty(style) && isEmpty(color)) {
      setSelectedKey(rows.filter(e => isFalse(e.isParent))[0]?.key);
    } else {
      setSelectedKey(rows.filter(e => isFalse(e.isParent)).find(e => e.style === style && e.color === color).key);
    }
  }, [rows, selectedKey, style, color]);

  const selectedRow = rows?.find(e => e.key === selectedKey);

  const { columnsCutSummary, headerRows } = useDeepMemo(() => getColumnsCutSummary(order, selectedRow), [order, selectedRow]);

  const rowsCutSummary = useDeepMemo(() => getRowsCutSummary(order, selectedRow, layRowData, false, rejectionData), [order, selectedRow, layRowData, rejectionData]);

  const onDownload = () => {
    const filename = "RECONCILIATION_REPORT.xlsx";

    const sbColCount = rows.map(row => getColumnsCutSummary(order, row)?.columnsCutSummary?.length || 0).sort((a, b) => b - a)[0];
    const finalMaxColNo = Math.max(summaryColumns?.length, fabUsageColumns?.length, sbColCount);

    const summaryCellList = convertTableToCell(summaryColumns, rows, 4, finalMaxColNo);
    const fabSummaryCellList = convertTableToCell(fabUsageColumns, rows, rows.length + 6, finalMaxColNo);

    let lastRowNo = (rows.length + 2) * 2 + 2;

    const cutSummaryCellList = rows.flatMap(row => {
      const { columnsCutSummary, headerRows } = getColumnsCutSummary(order, row);

      let headerCells = [
        { rowNo: lastRowNo + 1, colNo: 1, value: "Cut Summary", cellStyle: { hAlign: "left", bold: true, border: false, bgColor: "230,230,230" } },
        { rowNo: lastRowNo + 1, colNo: 2, value: row?.productName, cellStyle: { hAlign: "left", bgColor: "230,230,230" } },
        { rowNo: lastRowNo + 1, colNo: 3, value: row?.style, cellStyle: { hAlign: "left", bgColor: "230,230,230" } },
        { rowNo: lastRowNo + 1, colNo: 4, value: row?.color, cellStyle: { hAlign: "left", bgColor: "230,230,230" } },
        { rowNo: lastRowNo + 1, colNo: 5, colEnd: finalMaxColNo, cellStyle: { bgColor: "230,230,230" } }
      ];

      if (headerRows?.length > 1) {
        headerCells.push({ rowNo: lastRowNo + 2, colNo: 1, cellStyle: { bgColor: "99,172,238", color: "255,255,255" } });
        const groupRow = headerRows[0];
        Object.keys(groupRow).forEach(key => {
          const value = groupRow[key];
          const colNo = columnsCutSummary.findIndex(c => c.dataKey === key) + 1;
          const colEnd = colNo + columnsCutSummary.filter(c => c.dataKey?.split("_")[0] === value)?.length - 1;
          headerCells.push({
            rowNo: lastRowNo + 2,
            colNo,
            colEnd,
            value,
            cellStyle: { hAlign: "center", bgColor: "99,172,238", color: "255,255,255" }
          });
        });
        headerCells.push({
          rowNo: lastRowNo + 2,
          colNo: columnsCutSummary?.length,
          colEnd: finalMaxColNo,
          cellStyle: { bgColor: "99,172,238", color: "255,255,255" }
        });
      }
      const start = headerRows?.length > 1 ? lastRowNo + 3 : lastRowNo + 2;

      const rowList = getRowsCutSummary(order, row, layRowData, false, rejectionData);
      const sbCellList = convertTableToCell(columnsCutSummary, rowList, start, finalMaxColNo);
      const finalSbCellList = sbCellList.map(cell => ({
        ...cell,
        cellStyle: {
          ...cell?.cellStyle,
          hAlign: cell.colNo > 1 ? "center" : "left"
        }
      }));

      lastRowNo = start + rowList.length;

      return [...headerCells, ...finalSbCellList];
    });

    const unit = allUnitList.find(e => e.unitId === selectedRow?.unitIds) || {};

    const finalCellList = [
      {
        rowNo: 1,
        colNo: 1,
        value: unit?.displayName || unit?.name || tenant?.name || "",
        colEnd: finalMaxColNo,
        cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
        cellHeight: 48
      },
      {
        rowNo: 2,
        colNo: 1,
        value: "Reconciliation Report Summary",
        colEnd: finalMaxColNo,
        cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
        cellHeight: 36
      },
      { rowNo: 3, colNo: 1, value: "Order Summary", cellStyle: { hAlign: "left", bold: true, border: false, bgColor: "230,230,230" } },
      { rowNo: 3, colNo: 2, colEnd: finalMaxColNo, cellStyle: { bgColor: "230,230,230" } },
      ...summaryCellList,
      {
        rowNo: rows.length + 2 + 3,
        colNo: 1,
        value: "Fabric Usage Summary",
        cellStyle: { hAlign: "left", bold: true, border: false, bgColor: "230,230,230" }
      },
      { rowNo: rows.length + 2 + 3, colNo: 2, colEnd: finalMaxColNo, cellStyle: { bgColor: "230,230,230" } },
      ...fabSummaryCellList,
      ...cutSummaryCellList
    ];

    const excelSheet = {
      sheetNo: 0,
      sheetName: "Reconciliation Report",
      autoSizeColumn: false,
      excelData: { cellList: finalCellList }
    };

    const options = {
      url: `export/excel`,
      method: "post",
      responseType: "arraybuffer",
      params: { filename },
      data: [excelSheet]
    };

    download(options, {
      onSuccess: response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          const fName = header ? header.match(/filename="(.+)"/)[1] : filename;

          let blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = fName;
          link.click();
        }
      }
    });
  };

  const report = reportSummaryList.find(
    e =>
      e.productId === selectedRow?.productId &&
      e.style === selectedRow?.style &&
      e.color === selectedRow?.color &&
      selectedRow.effPartName?.toLowerCase() === e.effPartName?.toLowerCase()
  );

  const chartOptions = useDeepMemo(() => {
    if (isEmpty(report)) return {};
    return getSummaryEchartOptions({
      dataTypeList: [
        DataType.FABRIC_UTILIZATION_SUMMARY,
        DataType.WASTAGE_SUMMMARY,
        DataType.CONSUMPTION_SUMMARY,
        DataType.MARKER_EFFICIENCY,
        DataType.CUT_SUMMARY
      ],
      reportList: [{ ...report, status: selectedRow?.status }],
      uom: report.fabricUom
    });
  }, [report, selectedRow]);

  return {
    summaryColumns: summaryColumns.filter(c => !c.dataKey.endsWith("Cu") && c.dataKey !== "markerEfficiency"),
    rows,
    selectedKey,
    columnsCutSummary,
    headerRows,
    rowsCutSummary,
    report,
    chartOptions,
    downloading,
    onDownload,
    setSelectedKey
  };
};

export default useReconciliationSummary;
